import { useMemo, type ComponentType } from "react";
import { sanityBlogOverviewDocumentId, sanityCaseStudiesOverviewDocumentId, sanityWebinarsOverviewDocumentId, type SanityRouteDocument, type SanitySiteSettingsNavigation } from "@ouster/shared";
import { Seo } from "~/features/seo";
import { FaqPageJsonLd } from "~/features/seo/faq-json-ld";
import { buildOpenGraphImage } from "~/features/seo/utils";
import { FaqAnchorProvider } from "~/features/sanity/faq/context";
import { collectFaqEntries, toFaqSlugMap } from "~/features/sanity/faq/entries";
import { SanityAppLayout, adaptSanityRouteSettingsToAppLayoutProps } from "~/features/sanity/layout";
import { SanityBlogPageView } from "~/features/sanity/blog";
import type { SanityBlogPageData } from "~/features/sanity/blog/types";
import { SanityCaseStudiesPageView } from "~/features/sanity/case-studies";
import { SanityCaseStudyIntro } from "~/features/sanity/case-studies/case-study-intro";
import type { SanityCaseStudiesPageData } from "~/features/sanity/case-studies/types";
import { SanityWebinarsPageView } from "~/features/sanity/webinars";
import type { SanityWebinarsPageData } from "~/features/sanity/webinars/types";
import { SanityHero } from "~/features/sanity/hero";
import { SanityRouteSubNav } from "~/features/sanity/sub-nav";
import { ImageParallaxProvider } from "~/features/ui/image-parallax";
import { SanityPageBuilder, type SanityPageBuilderRenderSection } from "~/features/sanity/page-builder/renderer";
import { SanityWebinarWatch } from "~/features/sanity/webinar-watch";

/** Route document as serialized for the page props (raw `pageBuilder` stripped; sections loaded separately). */
export type SerializedSanityRoute = Omit<SanityRouteDocument, "pageBuilder">;
type PageBuilderComponent = ComponentType<{
  sections?: SanityPageBuilderRenderSection[];
}>;
export type SanityRouteViewProps = {
  mode?: "default" | "webinarWatch";
  route: SerializedSanityRoute;
  sections: SanityPageBuilderRenderSection[];
  siteSettings: SanitySiteSettingsNavigation | null;
  /** Server-loaded blog data; only set when `route` is the Blog overview page. */
  blogData?: SanityBlogPageData | null;
  /** Server-loaded listing data; only set when `route` is the Case Studies overview page. */
  caseStudiesData?: SanityCaseStudiesPageData | null;
  /** Server-loaded listing data; only set when `route` is the Webinars overview page. */
  webinarsData?: SanityWebinarsPageData | null;
  /**
   * Section renderer. Defaults to the static `SanityPageBuilder`; the live layer
   * (`LiveSanityRouteView`) injects the live-subscribing `LiveSanityPageBuilder`.
   */
  PageBuilderComponent?: PageBuilderComponent;
  /**
   * Remount key for the hero, set by the live layer when hero content changes.
   * Heroes are intro-animated, imperative components (GSAP SplitText, scroll
   * videos, three.js) that initialize once on mount, so a live edit must
   * re-mount them to render. Undefined (the static path) keeps normal React
   * reconciliation.
   */
  heroKey?: string;
};

/**
 * Shared render for a Sanity routable document: app layout (nav/footer from
 * site settings), SEO, the document hero, and its page-builder sections.
 * Used by the catch-all route and the gated cube route.
 */
export function SanityRouteView({
  route,
  sections,
  siteSettings,
  blogData,
  caseStudiesData,
  webinarsData,
  mode = "default",
  PageBuilderComponent = SanityPageBuilder,
  heroKey
}: SanityRouteViewProps) {
  const layoutProps = adaptSanityRouteSettingsToAppLayoutProps(route);
  const seo = route.seoMetadata;
  const ogImageSrc = seo?.image?.asset?.url;
  const isWebinarWatch = mode === "webinarWatch";
  const isBlogOverview = route._id === sanityBlogOverviewDocumentId && Boolean(blogData);
  const isCaseStudiesOverview = route._id === sanityCaseStudiesOverviewDocumentId && Boolean(caseStudiesData);
  const isWebinarsOverview = route._id === sanityWebinarsOverviewDocumentId && Boolean(webinarsData);
  const seoTitle = isWebinarWatch && route.title ? `Watch ${route.title}` : seo?.title || route.title || undefined;

  // Resolved once per route so anchors are unique across every FAQ section on
  // the page, and so the anchors and the JSON-LD are derived from the same pass.
  const faqEntries = useMemo(() => collectFaqEntries(sections), [sections]);
  const faqSlugMap = useMemo(() => toFaqSlugMap(faqEntries), [faqEntries]);
  return <FaqAnchorProvider value={faqSlugMap}>
      <SanityAppLayout siteSettings={siteSettings} {...layoutProps}>
        <Seo i18n={{
        href: route.href || undefined
      }} defaultSeo={{
        title: seoTitle,
        description: seo?.description || undefined,
        canonical: route.href || undefined,
        nofollow: isWebinarWatch || undefined,
        noindex: isWebinarWatch || undefined,
        openGraph: ogImageSrc ? {
          images: [buildOpenGraphImage(ogImageSrc, seo?.image?.asset?.dimensions)]
        } : undefined
      }} />
        <FaqPageJsonLd entries={faqEntries} href={route.href} />
        <SanityRouteSubNav route={route} sections={sections} siteSettings={siteSettings} />
        {isWebinarWatch ? <SanityWebinarWatch route={route} /> : isBlogOverview && blogData ? <SanityBlogPageView route={route} sections={sections} blogData={blogData} PageBuilderComponent={PageBuilderComponent} /> : isCaseStudiesOverview && caseStudiesData ? <SanityCaseStudiesPageView route={route} sections={sections} caseStudiesData={caseStudiesData} PageBuilderComponent={PageBuilderComponent} /> : isWebinarsOverview && webinarsData ? <SanityWebinarsPageView route={route} sections={sections} webinarsData={webinarsData} PageBuilderComponent={PageBuilderComponent} /> : <ImageParallaxProvider enabled={Boolean(route.enableImageParallax)}>
            <SanityHero key={heroKey} route={route} />
            {route._type === "caseStudy" && <SanityCaseStudyIntro metadata={route.caseStudyMetadata} />}
            <PageBuilderComponent sections={sections} />
          </ImageParallaxProvider>}
      </SanityAppLayout>
    </FaqAnchorProvider>;
}